import { CSSProperties, useEffect } from "react";
import style from "./payment-modal.module.css";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import consts, { Plan } from "shared/consts";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import tracking from "frontend/tracking";
import { BillingPeriod } from "./billing-period-picker";
import { numberWithCommas } from "frontend/utils/math-utils";
import { InvoicePreview } from "shared/datamodel/schemas/billing";
import BackArrow from "./back-arrow";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import dynamic from "next/dynamic";
import { CheckoutSource } from "./checkout";
import classNames from "classnames";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/nextjs";
import { getPathPrefix } from "../utils/getPathPrefix";
import { useBillingHooks } from "./billing-hooks";

const SELECTED_PLAN = Plan.pro;

const stripePromise = loadStripe(process.env.STRIPE_PKEY as string);

const CheckoutForm = dynamic(() => import("./checkout-form"));

export default function PaymentModal({
  showTrial,
  seatsCount,
  trialDays,
  priceByPlan,
  onDismiss,
  source,
}: {
  showTrial: boolean;
  seatsCount: number;
  trialDays: number;
  priceByPlan: Record<Plan, Record<BillingPeriod, number>>;
  onDismiss: () => void;
  source?: CheckoutSource;
}) {
  const {
    invoiceByPlanCache,
    yearlySaving,
    dropDownOptions,
    seatsMultiplyFactor,
    selectedPeriod,
    showCheckoutForm,
    stripeSecret,
    customerSessionClientSecret,
    loading,
    confirmPayment,
    message,
    seatsToBuy,
    invoicePreviewsAtomValue,
    setSelectedPeriod,
    setShowCheckoutForm,
    setConfirmPayment,
    setMessage,
    setSeatsToBuy,
    onPayButtonClicked,
  } = useBillingHooks({
    seatsCount,
    source,
    type: "modal",
  });

  useEffect(() => {
    const app = document.getElementById("__next");
    if (app) {
      app.style.filter = "blur(4px)";
    }
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "paymet-modal-showed", source);

    return () => {
      if (app) {
        app.style.filter = "unset";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderRadioButton(isSelected: boolean) {
    return (
      <div className={isSelected ? style.radioButtonSelected : style.radioButton}>
        {isSelected && <div className={style.radioButtonInnerCircle} />}
      </div>
    );
  }

  function renderPromotionTag() {
    return (
      <div className={style.promotionTag}>
        <span>{`BEST VALUE - SAVE $${numberWithCommas(Number.parseFloat(yearlySaving.toFixed(2)))}`}</span>
      </div>
    );
  }

  function renderPaymentType(paymentInterval: BillingPeriod) {
    const pricePerSeat = priceByPlan[SELECTED_PLAN][paymentInterval];
    const priceText = `$${numberWithCommas(Number.parseFloat(pricePerSeat.toFixed(2)))}`;

    const yearlyText = showTrial
      ? `${trialDays} days free, then ${priceText} a month per user`
      : `${priceText} a month per user`;

    const monthlyText = `${priceText} a month per user`;

    const text = paymentInterval === "year" ? yearlyText : monthlyText;

    return invoicePreviewsAtomValue ? (
      <div
        key={paymentInterval}
        onClick={() => setSelectedPeriod(paymentInterval)}
        className={style.paymentTypeInnerContainer}
      >
        {renderRadioButton(paymentInterval === selectedPeriod)}
        <div className={style.paymentType}>
          <div className={style.flexContainer}>
            <span className={style.paymentTypeText}>{paymentInterval === "year" ? "Yearly" : "Monthly"}</span>
            {paymentInterval === "year" && !!yearlySaving && renderPromotionTag()}
          </div>
          <div className={style.priceContainer}>
            <span className={style.paymentTypePrice}>{text}</span>
          </div>
        </div>
      </div>
    ) : (
      <Skeleton key={paymentInterval} width={"93%"} height={47} className={style.paymentTypeInnerContainer} />
    );
  }

  function renderDueNextPeriod(planInvoicePreview: InvoicePreview | null, nextPeriodFormattedDate: string) {
    const price = (planInvoicePreview?.dueToday ?? 0) * seatsMultiplyFactor;
    return (
      <>
        {planInvoicePreview ? (
          <div className={style.nextMonthLineItem}>
            <span style={{ fontWeight: "600" }}>{`${nextPeriodFormattedDate}`}</span>
            <div className={style.nextPeriodLineItem}>
              <span>{`$${numberWithCommas(
                Number.parseFloat(selectedPeriod === "year" ? (price / 12).toFixed(2) : price.toFixed(2))
              )} per month`}</span>
            </div>
          </div>
        ) : (
          <Skeleton width={"93%"} height={24} />
        )}
      </>
    );
  }

  function renderInvoiceDetails(pricePerSeat: number, showFreeTrial?: boolean, customStyle?: CSSProperties) {
    const usersCountText = `${numberWithCommas(seatsToBuy)} ${seatsToBuy > 1 ? "users" : "user"}`;
    const planInvoicePreview = invoiceByPlanCache && invoiceByPlanCache[selectedPeriod];
    const invoiceBreakDownText = planInvoicePreview ? `($${pricePerSeat.toFixed(2)} / month x ${usersCountText})` : "";
    return (
      <div style={customStyle} className={style.invoceDetailsContainer}>
        <div className={style.flexColumnContainer}>
          {renderDueNextPeriod(planInvoicePreview, "Subtotal")}
          <span className={style.invoiceBreakDownText}>{invoiceBreakDownText}</span>
        </div>
        {showFreeTrial && (
          <div className={style.flexColumnContainer}>
            <span>Due today</span>
            <div className={style.freeLineItem}>
              <span>{`${trialDays} days free`}</span>
              <span>$0</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderPaymentIntervalOptions() {
    return (
      <div className={style.paymentTypeContainer}>
        {["year", "month"].map((interval) => {
          return renderPaymentType(interval as "year" | "month");
        })}
      </div>
    );
  }

  function renderDropDownPicker() {
    return (
      <div className={style.dropDownContainer} data-testid={"checkout seats count"}>
        <DropdownPicker<number>
          options={dropDownOptions}
          titles={(seatsCount) => `${seatsCount}`}
          optionRenderer={(seatsCount) => {
            return { element: <span>{`${seatsCount.option} seats`}</span>, enabled: true };
          }}
          onChange={(selectedSeats) => setSeatsToBuy(selectedSeats)}
          isSelected={(v) => v === seatsToBuy}
          enabled={true}
          scrollToSelected={true}
          optionsPickerStyle={{ maxHeight: "296px" }}
          pickerCustomIcon={getPathPrefix("/images/chevron-black.svg")}
          pickerCustomStyle={{
            borderRadius: "6px",
          }}
          optionsContainerStyle={{ fontSize: 16, color: "#113357", borderRadius: "6px" }}
        />
      </div>
    );
  }

  function renderSeatsSelection() {
    return (
      <div className={style.seatsSelectionContainer}>
        <div className={style.seatsSelectionContent}>
          <span>How many seats?</span>
          {renderDropDownPicker()}
        </div>
      </div>
    );
  }

  function renderPaymentDetails() {
    return (
      <>
        {renderSeatsSelection()}
        {renderPaymentIntervalOptions()}
        {renderInvoiceDetails(priceByPlan[SELECTED_PLAN][selectedPeriod], false)}
      </>
    );
  }

  function renderContinueButton() {
    return (
      <div className={style.ctaButton}>
        {invoicePreviewsAtomValue ? (
          <button onClick={onPayButtonClicked} className={style.continueButton}>
            {loading ? "Loading..." : "Continue"}
          </button>
        ) : (
          <Skeleton width={"93%"} className={style.continueButtonSkeleton} />
        )}
      </div>
    );
  }

  function renderPayButton() {
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "confirm-payment", SELECTED_PLAN);
    return stripeSecret ? (
      <div className={classNames(style.ctaButton, { [style.ctaWithMessage]: message })}>
        <button onClick={() => (confirmPayment ? {} : setConfirmPayment(true))} className={style.continueButton}>
          {confirmPayment ? "Loading..." : "Complete purchase"}
        </button>
        {
          <span className={classNames(style.paymentMessage, { [style.showPaymentMessage]: message })}>
            {message ?? null}
          </span>
        }
      </div>
    ) : (
      <Skeleton containerClassName={style.checkoutSkeletonButton} count={1} width={460} height={36} />
    );
  }

  function renderCheckoutSkeleton() {
    return (
      <div className={style.checkoutSkeletonContainer}>
        <Skeleton containerClassName={style.checkoutSkeleton} count={4} width={460} height={44} />
      </div>
    );
  }

  function renderStripeCheckout() {
    return stripeSecret ? <>{renderPaymentForm()}</> : renderCheckoutSkeleton();
  }

  function renderPaymentForm() {
    return (
      <Elements
        options={{
          clientSecret: stripeSecret ?? undefined,
          appearance: { theme: "stripe" },
          customerSessionClientSecret: customerSessionClientSecret ?? undefined,
        }}
        stripe={stripePromise}
      >
        <CheckoutForm
          customStyle={{
            width: "100%",
            minWidth: "unset",
            padding: "0",
            paddingTop: "40px",
          }}
          confirmPayment={confirmPayment}
          onLoadingChanged={(isLoading) => {
            if (!isLoading) {
              setConfirmPayment(false);
            }
          }}
          onError={(error) => {
            console.error("Stripe error", error);
          }}
          amount={invoiceByPlanCache ? invoiceByPlanCache[selectedPeriod].dueNextPeriod : 0} //track the subscription's non-prorated value
          setMessage={setMessage}
        />
      </Elements>
    );
  }

  function renderSmallModalRightSection() {
    const rightSectionText =
      source?.name === "templates"
        ? `Unlock ${source.value} and 100+ premium templates`
        : "Unlock Pro with 100+ premium templates";
    return (
      <>
        <span className={style.rightSectionTitle}>{rightSectionText}</span>
        <img className={style.image} src={getPathPrefix("/images/checkout/checkout-templates.svg")} alt="templates" />
      </>
    );
  }

  function renderSmallModal() {
    return (
      <div className={style.container} data-testid="checkout-container">
        {!showCheckoutForm && (
          <div data-testid="close-payment-modal">
            <CloseIcon
              style={{ cursor: "pointer", position: "absolute", top: "16px", right: "16px", zIndex: "100" }}
              onClick={onDismiss}
            />
          </div>
        )}
        <div className={style.rightSection}>{renderSmallModalRightSection()}</div>
        <ErrorBoundary
          fallback={
            <div className={style.leftSection}>
              <h1>Oops, something went wrong...</h1>
            </div>
          }
          onError={(error, info) => {
            console.error("Payment error", error, info);
            tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "payment-modal-error", error.message, source?.name);
            Sentry.addBreadcrumb({
              message: "Error rendering checkout form (modal)",
              category: "error",
              level: "error",
            });
            Sentry.captureException(error, { tags: { type: "billing", page: "payment-modal" } });
          }}
        >
          <div className={style.leftSection}>
            {showCheckoutForm && (
              <BackArrow
                onClick={() => {
                  setShowCheckoutForm(false);
                  setMessage(null);
                }}
                customStyle={{ cursor: "pointer", position: "absolute", top: "12px", left: "16px", zIndex: "100" }}
              />
            )}
            <div className={style.checkoutWrapper}>
              <span className={style.checkoutHeader}>Upgrade To WorkCanvas Pro</span>
              <div className={classNames(style.checkoutContainer)}>
                {!showCheckoutForm && renderPaymentDetails()}
                {showCheckoutForm && renderStripeCheckout()}
              </div>
            </div>
            {showCheckoutForm ? renderPayButton() : renderContinueButton()}
          </div>
        </ErrorBoundary>
      </div>
    );
  }

  return renderSmallModal();
}
